import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { supabase } from "@/integrations/supabase/client";
import { SOFT_RETIRED_SLUGS } from "@/config/softRetiredPosts";

interface HomeBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  image_url: string | null;
  category: string;
  read_time: string | null;
  published_at: string | null;
  created_at: string;
}

const MAX_POSTS = 12;
const AUTO_ADVANCE_MS = 5000;

export default function HomeBlogSection() {
  const { t, language } = useLanguage();
  const { localePath } = useLocalizedPath();
  const [posts, setPosts] = useState<HomeBlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const section = (t as any).home?.blogSection;
  const blogLang = language === "fr" ? "fr" : "en";

  // Determine how many cards are visible at once
  useEffect(() => {
    const updateItemsPerView = () => {
      const width = window.innerWidth;
      if (width >= 1024) setItemsPerView(5);
      else if (width >= 640) setItemsPerView(2);
      else setItemsPerView(1);
    };
    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  // Fetch posts
  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      setIsLoading(true);

      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, image_url, category, read_time, published_at, created_at")
        .eq("published", true)
        .eq("language", blogLang)
        .order("published_at", { ascending: false })
        .limit(MAX_POSTS);

      if (error || cancelled) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      const visible = (data || []).filter((p) => !SOFT_RETIRED_SLUGS.has(p.slug));

      if (!cancelled) {
        setPosts(visible);
        setIsLoading(false);
      }
    };

    fetchPosts();
    return () => { cancelled = true; };
  }, [blogLang]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    posts.forEach((p) => cats.add(p.category));
    return Array.from(cats).sort();
  }, [posts]);

  const filteredPosts = useMemo(() => {
    if (activeCategory === "all") return posts;
    return posts.filter((p) => p.category === activeCategory);
  }, [posts, activeCategory]);

  const maxIndex = Math.max(0, filteredPosts.length - itemsPerView);

  // Reset index when filter or viewport changes
  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxIndex));
  }, [activeCategory, itemsPerView, maxIndex]);

  // Auto-advance carousel
  useEffect(() => {
    if (filteredPosts.length <= itemsPerView || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    }, AUTO_ADVANCE_MS);

    return () => clearInterval(interval);
  }, [filteredPosts.length, itemsPerView, maxIndex, isPaused]);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
  }, [maxIndex]);

  const prev = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  const next = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  if (isLoading || posts.length === 0 || !section) return null;

  // On desktop (5 visible), add one peeking card on each side, faded by the edge overlays
  const hasPeek = itemsPerView === 5;
  const slots = hasPeek ? itemsPerView + 2 : itemsPerView;
  const translatePercent = -(currentIndex * (100 / slots));

  return (
    <section
      className="py-10 md:py-14 bg-background border-t border-border/40"
      aria-labelledby="home-blog-heading"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <div className="container">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="text-label text-gold mb-1">{section.subtitle}</p>
            <h2 id="home-blog-heading" className="text-display text-2xl md:text-3xl text-foreground">
              {section.title}
            </h2>
          </div>

          {categories.length > 1 && (
            <div
              className="flex flex-wrap gap-2"
              role="tablist"
              aria-label={language === "fr" ? "Filtrer par catégorie" : "Filter by category"}
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === "all"}
                onClick={() => setActiveCategory("all")}
                className={`px-3 py-1 text-xs tracking-wider uppercase transition-colors border ${
                  activeCategory === "all"
                    ? "bg-gold text-black border-gold"
                    : "bg-transparent text-muted-foreground border-border hover:border-gold/50 hover:text-gold"
                }`}
              >
                {section.filterAll}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={activeCategory === cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1 text-xs tracking-wider uppercase transition-colors border ${
                    activeCategory === cat
                      ? "bg-gold text-black border-gold"
                      : "bg-transparent text-muted-foreground border-border hover:border-gold/50 hover:text-gold"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </header>

        <div className="relative">
          {/* Edge fade overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-background to-transparent z-[5] pointer-events-none" aria-hidden="true" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-background to-transparent z-[5] pointer-events-none" aria-hidden="true" />

          {/* Carousel track */}
          <div ref={trackRef} className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-elegant"
              style={{
                transform: `translateX(${translatePercent}%)`,
                ...(hasPeek
                  ? { width: `${(slots / itemsPerView) * 100}%`, marginLeft: `-${100 / slots}%` }
                  : {}),
              }}
            >
              {filteredPosts.map((post) => (
                <article
                  key={post.id}
                  className="group flex-shrink-0 px-2"
                  style={{ width: `${100 / slots}%` }}
                >
                  <Link
                    to={localePath(`/blog/${post.slug}`)}
                    className="block border border-border bg-card/40 hover:border-gold/40 transition-colors duration-300"
                    aria-label={`${section.readMore} – ${post.title}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={post.image_url || "/placeholder.svg"}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-label text-gold text-[10px] tracking-[0.15em] uppercase mb-2">
                        {post.category}
                      </p>
                      <h3 className="text-display text-base text-foreground line-clamp-2 group-hover:text-gold transition-colors">
                        {post.title}
                      </h3>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>

          {/* Navigation arrows */}
          {filteredPosts.length > itemsPerView && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label={language === "fr" ? "Article précédent" : "Previous article"}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 hidden md:flex items-center justify-center w-10 h-10 border border-gold/30 bg-background text-gold hover:bg-gold hover:text-black transition-colors z-10"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label={language === "fr" ? "Article suivant" : "Next article"}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 hidden md:flex items-center justify-center w-10 h-10 border border-gold/30 bg-background text-gold hover:bg-gold hover:text-black transition-colors z-10"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        {filteredPosts.length > itemsPerView && maxIndex > 0 && (
          <div className="flex items-center justify-center gap-2 mt-6" role="tablist" aria-label={language === "fr" ? "Position du carrousel" : "Carousel position"}>
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={currentIndex === i}
                aria-label={`${language === "fr" ? "Position" : "Position"} ${i + 1}`}
                onClick={() => goTo(i)}
                className={`w-2 h-2 transition-colors ${
                  currentIndex === i ? "bg-gold" : "bg-gold/30 hover:bg-gold/50"
                }`}
              />
            ))}
          </div>
        )}

        {/* View all link */}
        <div className="text-center mt-8">
          <Link
            to={localePath("/blog")}
            className="inline-flex items-center gap-2 text-gold hover:text-gold/80 transition-colors text-xs tracking-wide uppercase"
          >
            {section.viewAll}
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </section>
  );
}
